import { useCallback, useEffect, useState } from "react";
import {
  ChannelSound,
  DEFAULT_NOTIFICATION_SOUND_SETTINGS,
  NotificationChannel,
} from "@/utils/notificationSounds";

const STORAGE_KEY = "notification.sound.settings";

export type NotificationSoundSettings = Record<NotificationChannel, ChannelSound>;

/**
 * Merge stored (possibly partial / older-shape) settings over defaults so each
 * channel always has a valid `{ soundId, volume }` and bad data falls back.
 */
function parseSettings(raw: string | null): NotificationSoundSettings {
  if (!raw) return DEFAULT_NOTIFICATION_SOUND_SETTINGS;
  try {
    const parsed = JSON.parse(raw) as Partial<
      Record<NotificationChannel, Partial<ChannelSound>>
    >;
    const channels: NotificationChannel[] = [
      "headlines",
      "truthSocial",
      "portfolio",
    ];
    return channels.reduce((acc, channel) => {
      acc[channel] = {
        ...DEFAULT_NOTIFICATION_SOUND_SETTINGS[channel],
        ...(parsed[channel] ?? {}),
      };
      return acc;
    }, {} as NotificationSoundSettings);
  } catch {
    return DEFAULT_NOTIFICATION_SOUND_SETTINGS;
  }
}

/**
 * Per-channel notification sound + volume, stored locally. Mirrors
 * `useTruthSocialNotificationSettings`: lazy init, write-back on change,
 * cross-tab sync via the `storage` event.
 */
export function useNotificationSoundSettings() {
  const [settings, setSettings] = useState<NotificationSoundSettings>(() => {
    try {
      return parseSettings(localStorage.getItem(STORAGE_KEY));
    } catch {
      return DEFAULT_NOTIFICATION_SOUND_SETTINGS;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage not available
    }
  }, [settings]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSettings(parseSettings(e.newValue));
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const updateChannel = useCallback(
    (channel: NotificationChannel, patch: Partial<ChannelSound>) => {
      setSettings((prev) => ({
        ...prev,
        [channel]: { ...prev[channel], ...patch },
      }));
    },
    []
  );

  return { settings, updateChannel };
}
